import React, { useState } from "react";
import {
  Upload,
  Package,
  Search,
  SlidersHorizontal,
  Plus,
  ArrowRight,
  Image,
  FileText,
  AlertTriangle,
  CheckCircle,
  Download,
  Layers,
  Tag,
  Play
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { useDispatch, useSelector } from "react-redux";
import { setIsGetStartedDialogOpen } from "../store/flowSlice";
import { RootState } from "../store/store";
import { compareDashboardUserState } from "../store/userSlice";
import { useImportShopify } from "../hooks/useImportShopify";
import { useNavigate } from 'react-router-dom';
import { analytics } from "../analytics";
import { type AuthUser } from "wasp/auth";
import { useTranslation, TranslationKey } from "../i18n/hooks";


const ProductsEmptyView = ({ state = "empty", user } :{
  state?: "empty" | "products";
  user: AuthUser;
}) => {
  const { t } = useTranslation();
  const dashboardUserState= useSelector((state: RootState) => state.user.currentCollection.dashboardUserState);
  const navigate = useNavigate();
  const { importShopify, isImportLoading } = useImportShopify();
  const dispatch = useDispatch();
  const [showVideo, setShowVideo] = useState(false);
  
  const EmptyState = () => (
    <div className="mx-auto space-y-6">
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
        <CardContent className="py-12">
          <div className="text-center space-y-6">
            <Package className="w-12 h-12 mx-auto text-blue-500" />
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">
                {t("products.journey_starts_here" as any as TranslationKey)}
              </h2>
              <p className="text-gray-600 max-w-md mx-auto">
                {t("products.import_see_score" as any as TranslationKey)}
              </p>
            </div>

            {compareDashboardUserState(dashboardUserState, "initial") == 0 && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  className="flex items-center gap-2"
                  onClick={() => {
                    analytics.sendAnalysisButtonClickedEvent({
                      entryPoint: "products_page",
                      email: user?.email || user.id,
                    });
                    dispatch(setIsGetStartedDialogOpen(true));
                  }}
                >
                  <Package className="w-4 h-4" />
                  {t("dashboard.connect_shopify" as any as TranslationKey)}
                </Button>
              </div>
            )}
            {compareDashboardUserState(
              dashboardUserState,
              "connectedShopify"
            ) == 0 && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  className="flex items-center gap-2"
                  onClick={async () => {
                    await importShopify();
                    navigate("/admin/dashboard");
                  }}
                  disabled={isImportLoading}
                >
                  <Package className="w-4 h-4" />
                  {t("products.import_from_store" as any as TranslationKey)}
                </Button>
              </div>
            )}
            {compareDashboardUserState(
              dashboardUserState,
              "importInProgress"
            ) == 0 && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="flex items-center gap-2" disabled={true}>
                  <Package className="w-4 h-4" />
                  {t("products.being_imported" as any as TranslationKey)}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("products.what_to_expect" as any as TranslationKey)}</CardTitle>
            <CardDescription>{t("dashboard.after_analyzing" as any as TranslationKey)}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <div className="font-medium">{t("products.instant_analysis" as any as TranslationKey)}</div>
                <div className="text-sm text-gray-500">
                  {t("products.see_content_measures" as any as TranslationKey)}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <div className="font-medium">{t("products.smart_collections" as any as TranslationKey)}</div>
                <div className="text-sm text-gray-500">
                  {t("products.group_products" as any as TranslationKey)}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <div className="font-medium">{t("products.ai_enhancement_tools" as any as TranslationKey)}</div>
                <div className="text-sm text-gray-500">
                  {t("products.one_click_improvements" as any as TranslationKey)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("products.see_in_action" as any as TranslationKey)}</CardTitle>
            <CardDescription>{t("products.quick_preview" as any as TranslationKey)}</CardDescription>
          </CardHeader>
          <CardContent>
            {!showVideo ? (
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                <Button 
                  variant="outline" 
                  onClick={() => setShowVideo(true)}
                  className="flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  {t("dashboard.watch_demo" as any as TranslationKey)}
                </Button>
              </div>
            ) : (
              <div style={{ position: "relative", overflow: "hidden", aspectRatio: "1920/1080" }}>
                <iframe 
                  src="https://share.synthesia.io/embeds/videos/0f3ff404-fbf9-4acc-915a-85f5a633498f" 
                  loading="lazy" 
                  title="Synthesia video player - Marketplace IQ Content Enhancement" 
                  allowFullScreen 
                  allow="encrypted-media; fullscreen;" 
                  style={{ position: "absolute", width: "100%", height: "100%", top: 0, left: 0, border: "none", padding: 0, margin: 0, overflow: "hidden" }}
                />
              </div>
            )}
          </CardContent>
          
        </Card>
      </div>
    </div>
  );

  const ProductsState = () => (
    <div className="space-y-6">
      {/* Success Message for First Upload */}
      <Alert className="bg-green-50 border-green-100">
        <CheckCircle className="w-4 h-4 text-green-600" />
        <AlertTitle>{t("products.import_success" as any as TranslationKey)}</AlertTitle>
        <AlertDescription>
          {t("products.analyzed_opportunities" as any as TranslationKey)}
        </AlertDescription>
      </Alert>

      {/* Filter and Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-1 gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
            <input
              type="text"
              placeholder={t("productsView.search_products" as any as TranslationKey)}
              className="w-full pl-9 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {t("products.filters" as any as TranslationKey)}
          </Button>
          <Button
            variant="outline"
            className="flex items-center gap-2"
          >
            <Layers className="w-4 h-4" />
            {t("products.collections" as any as TranslationKey)}
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {t("products.export" as any as TranslationKey)}
          </Button>
          <Button className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            {t("products.add_products" as any as TranslationKey)}
          </Button>
        </div>
      </div>

      {/* Content Score Overview */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              {t("products.overall_content_score" as any as TranslationKey)}
              <span className="text-xl text-blue-600">72/100</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Progress
              value={72}
              className="h-2"
            />
            <p className="text-sm text-gray-500 mt-2">
              {t("products.content_performing_well" as any as TranslationKey)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              {t("dashboard.products" as any as TranslationKey)}
              <span className="text-xl">2,451</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t("products.with_images" as any as TranslationKey)}</span>
              <span className="font-medium">1,967 (80%)</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t("products.complete_description" as any as TranslationKey)}</span>
              <span className="font-medium">2,100 (86%)</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              {t("products.quick_actions" as any as TranslationKey)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                {t("products.enhance_images" as any as TranslationKey)}
              </span>
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                {t("products.generate_tags" as any as TranslationKey)}
              </span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Product Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="aspect-square bg-gray-100 rounded-lg relative">
                  <div className="absolute top-2 right-2">
                    <div className="bg-white rounded-full px-2 py-1 text-sm font-medium flex items-center gap-1">
                      <span>68</span>
                      <span className="text-xs text-gray-500">/100</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium">{t("products.product_name" as any as TranslationKey)} {i + 1}</h3>
                  <div className="flex gap-2">
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                      {t("products.needs_images" as any as TranslationKey)}
                    </span>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      {t("products.good_description" as any as TranslationKey)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {state === "empty" ? <EmptyState /> : <ProductsState />}
    </div>
  );
};

export default ProductsEmptyView;
